// Real data sourced from ePIC Investor Deck v3.6, Financial Model v1, and Risk Factors doc.

export const COMPANY = {
  name: "ePIC Services Company",
  legalName: "ePIC Services Company, Inc.",
  ticker: "ePIC",
  tagline: "A revolutionary estate and legacy planning company for the digital age.",
  round: "Convertible Note",
  raise: "$2.5M",
  instrument: "Unsecured Convertible Promissory Note",
  conversionPrice: "$4.50",
  minTicket: "$50K",
  debtCap: "$7.5M",
  contactName: "Carter Wilcoxson",
  contactRole: "CEO & Founder",
  phone: "855.777.ePIC (3742)",
  email: "carter@epicservicescompany.com",
  meetingUrl: "https://epicservicescompany.com/meet-with-carter-wilcoxson-667187",
  noteUrl: "/docs/ePIC-Convertible-Promissory-Note.pdf",
  website: "https://epicservicescompany.com/home",
  modelUrl: "https://docs.google.com/spreadsheets/d/1sfQAIlckFiL3jwvJRXX0flpvk2eYsA-k/edit?usp=sharing&ouid=109673874582697241819&rtpof=true&sd=true",
  signUrl: "https://acrobat.adobe.com/id/urn:aaid:sc:US:18ad6f0a-a58a-4581-9c68-2ac54a375336",
};

export const HERO_LINES = ["Invest in", "ePIC Services", "Company"];

export const MARQUEE_ITEMS = [
  "$124T Wealth Transfer",
  "61M Underserved",
  "52% Close Rate",
  "+234% YoY Revenue",
  "1.5M Advisors",
  "Turnkey Estate Planning",
  "$2.5M Convertible Note",
];

// Investment thesis / manifesto chapters
export const THESIS = [
  {
    n: "01",
    title: "The problem",
    body: "61 million Americans need estate planning, yet 83% have no estate or trust plan in place. Specialists are too expensive, digital tools too limited — and families still want a human advisor. Meanwhile, $124 trillion in wealth is about to transfer to the next generation.",
  },
  {
    n: "02",
    title: "Our solution",
    body: "ePIC is the most innovative B2B2C estate planning platform ever built — a turnkey system that helps financial advisors and insurance agents reduce client-acquisition costs and grow revenue through attorney-backed planning, funded trusts, and legacy preservation, all in one integrated ecosystem.",
  },
  {
    n: "03",
    title: "Why now",
    body: "America is on the cusp of the greatest wealth transfer in its history. ePIC turns 1.5 million independent advisors and insurance agents into a national distribution engine — at exactly the moment the market needs it most.",
  },
];

export const SOLUTION_PILLARS = [
  { label: "Digital Workflow", note: "A guided, customized estate plan built through a seamless digital experience." },
  { label: "National Specialists", note: "Human, personalized support from licensed specialists across the country." },
  { label: "Family Stories Vault", note: "Legacy preservation that creates an emotional connection and deepens retention." },
];

export const REVENUE_STREAMS = [
  { price: "$7,497", unit: "per advisor kit", label: "Partner Kits", note: "Advisor Partner onboarding fees." },
  { price: "$2,700", unit: "per implementation", label: "eStatePlan™ Fees", note: "Implementation fees per funded plan." },
  { price: "$147", unit: "per month", label: "Advisor Subscriptions", note: "Recurring hosting & platform services." },
  { price: "$3,500", unit: "per event / advisor", label: "Educational Workshops", note: "Ongoing, recurring estate and legacy planning events." },
];

// Recurring override & referral engines (basis, not a fixed per-unit price)
export const REVENUE_ENGINES = [
  { basis: "Override", label: "Bridge Overrides", note: "Override revenue on partner insurance premium production." },
  { basis: "Override", label: "BLP Overrides", note: "Override revenue on brokerage & life-product production." },
  { basis: "Recurring", label: "RIA Referral", note: "Recurring economics on referred assets under management." },
  { basis: "2% Override", label: "LegacyTree", url: "https://www.legacytreefoundation.org/for-financial-advisors", note: "2% on qualifying production — $1M trigger, $10K retroactive true-up." },
  { basis: "8% Recruiter Fee", label: "Financial Gravity", url: "https://financialgravity.com/", logo: "/brand/financial-gravity.png", note: "Advisor recruiting referral — 8% of AUM-based advisory fees for 12 months per recruited advisor ($800/mo on a $12M-AUM advisor)." },
  { basis: "2.1% Blended Rate", label: "Agent Commissions", note: "Direct agent commissions — Q3 2026 actuals: $14.3K on $675K premium across 7 cases; modeled to $583K/yr by Yr 5 (base)." },
  { basis: "3% Effective", label: "Taxes Saved", featured: true, logo: "/brand/taxes-saved.png", url: "https://www.taxessaved.com/", note: "A revenue stream built on real expertise: Taxes Saved connects the platform to former Big Four CPAs and top tax strategists, delivering seamless, proactive tax-savings analysis to every client on the platform. The result is a genuine outcome, clients keep more of what they've worked so hard to earn and protect more of what they've worked so hard to save, and a recurring revenue line for the company: 15% of ePIC's 20% share of every dollar of realized client tax savings." },
];

export const GTM = [
  { value: "1.5M+", label: "Independent advisors & agents in reach" },
  { value: "$300", label: "Commission per eStatePlan™ sold" },
  { value: "52%", label: "Proven YTD close rate" },
  { value: "10 hrs", label: "Advisor sales training to activate" },
];

// Financial scenarios — Total modeled revenue ($M) by year
export const SCENARIOS = {
  downside: {
    key: "downside",
    label: "Downside",
    multiple: "5×",
    y5rev: "$35.7M",
    y5ebitda: "$8.9M",
    equity: "$44.6M",
    data: [
      { year: "Yr 1", revenue: 1.30 },
      { year: "Yr 2", revenue: 3.92 },
      { year: "Yr 3", revenue: 9.30 },
      { year: "Yr 4", revenue: 19.36 },
      { year: "Yr 5", revenue: 35.71 },
    ],
  },
  base: {
    key: "base",
    label: "Base",
    multiple: "8×",
    y5rev: "$74.2M",
    y5ebitda: "$26.0M",
    equity: "$207.9M",
    data: [
      { year: "Yr 1", revenue: 2.82 },
      { year: "Yr 2", revenue: 8.33 },
      { year: "Yr 3", revenue: 19.63 },
      { year: "Yr 4", revenue: 40.54 },
      { year: "Yr 5", revenue: 74.24 },
    ],
  },
  upside: {
    key: "upside",
    label: "Upside",
    multiple: "10×",
    y5rev: "$128.2M",
    y5ebitda: "$57.7M",
    equity: "$577.0M",
    data: [
      { year: "Yr 1", revenue: 5.15 },
      { year: "Yr 2", revenue: 14.95 },
      { year: "Yr 3", revenue: 34.70 },
      { year: "Yr 4", revenue: 70.83 },
      { year: "Yr 5", revenue: 128.22 },
    ],
  },
};

export const KPIS = [
  { label: "Trailing 365-Day Revenue", value: "$1.2M" },
  { label: "YoY Revenue Growth", value: "+234%" },
  { label: "2025 Gross Revenue", value: "$505K" },
  { label: "Advisors Onboarded", value: "62" },
  { label: "Close Rate", value: "52%" },
  { label: "Conversion Price", value: "$4.50" },
];

export const MARKET = [
  { label: "Estate Planning Market — 2023", value: "$15B", note: "Estate planning services market size" },
  { label: "Market CAGR", value: "5.5%", note: "Projected annual growth rate" },
  { label: "Projected by 2032", value: "$25B", note: "Estate planning services market size" },
];

export const MARKET_CONTEXT = [
  { value: "$124T", label: "Total estate wealth transferring generationally" },
  { value: "83%", label: "Of Americans have no estate or trust plan" },
  { value: "61M", label: "Underserved Americans who need planning" },
];

// Convertible note terms
export const NOTE_TERMS = [
  "10% annual interest, paid quarterly (interest-only)",
  "5-year maturity",
  "Conversion price fixed at $4.50 / share",
  "Up to 40% bonus shares on full conversion",
  "Unlimited prepayment without penalty",
  "Multiple conversion options, including quarterly partials",
  "Automatic conversion upon default to protect investors",
  "Debt cap protection — ePIC cannot exceed $7.5M total debt",
];

// Investor instructions for completing the DocuSign PowerForm
export const SIGN_STEPS = [
  {
    n: "01",
    title: "Open the secure form",
    body: "Click “Sign the Note Online” to launch the Adobe Acrobat Sign form. No account or printing required.",
  },
  {
    n: "02",
    title: "Enter your details",
    body: "Provide your full legal name, email, and mailing address — plus your entity name if you're investing through a trust, IRA, or company.",
  },
  {
    n: "03",
    title: "Set your investment",
    body: "Enter your Investment Amount (minimum $50,000) and select your bonus-share election. Review the terms: 10% interest, $4.50 conversion, 5-year maturity.",
  },
  {
    n: "04",
    title: "Sign & finish",
    body: "Sign and date electronically, then click Finish. You'll instantly receive a signed copy by email, and our team will follow up with wire instructions.",
  },
];


// Illustrative return multiples on a $100K note, by bonus-share level and exit scenario (5-year exit).
export const RETURNS_MATRIX = {
  scenarios: ["Downside", "Base", "Upside"],
  rows: [
    { bonus: "Low (0%)", values: ["$240K", "$1.12M", "$3.10M"], multiples: ["2.4×", "11.2×", "31.0×"] },
    { bonus: "Mid (20%)", values: ["$288K", "$1.34M", "$3.72M"], multiples: ["2.9×", "13.4×", "37.2×"] },
    { bonus: "Max (40%)", values: ["$336K", "$1.56M", "$4.34M"], multiples: ["3.4×", "15.6×", "43.4×"] },
  ],
};

// Convertible note calculator config.
// exits.equityM mirrors SCENARIOS[*].equity (5-year modeled equity value, $M) — keep in sync with the financial model.
export const NOTE_CALC = {
  convPrice: 4.5,
  interestRate: 0.1,
  termYears: 5,
  min: 50000,
  presets: [50000, 100000, 250000, 500000, 1000000],
  bonuses: [
    { label: "Low", value: 0 },
    { label: "Mid", value: 0.2 },
    { label: "Max", value: 0.4 },
  ],
  exits: [
    { key: "downside", label: "Downside", equityM: 44.6 },
    { key: "base", label: "Base", equityM: 207.9 },
    { key: "upside", label: "Upside", equityM: 577.0 },
  ],
};

export const FUND_PILLARS = [
  { label: "Marketing & Staffing", note: "Fuel advisor onboarding and national demand generation." },
  { label: "Tech & Platform", note: "Deepen the estate-planning platform and product moat." },
  { label: "General & Administrative", note: "Scale the operating backbone of the business." },
  { label: "Operations", note: "Implementation, trust funding, and specialist support." },
];

// Direct equity alternative to the convertible note
export const EQUITY = {
  price: "$3.15",
  priceNum: 3.15,
  notePrice: "$4.50",
  minTicket: "$10K",
  docUrl: "/docs/ePIC-Direct-Stock-Purchase-Agreement.pdf",
  exitPrices: [5, 8, 15, 20, 30, 60],
  fdShares: 4133171,
  signUrl: "https://acrobat.adobe.com/id/urn:aaid:sc:US:450c43db-1867-4934-ada5-53c71cfeae7a",
  points: [
    "Invest directly in ePIC equity — no note, no conversion mechanics or maturity.",
    "Shares priced at $3.15 — a discount to the $4.50 note conversion price.",
    "Become a shareholder from day one, with full upside participation.",
    "Minimum investment of $10,000.",
  ],
  example: { amount: "$100,000", shares: "≈ 31,746 shares" },
};

export const MILESTONES = [
  {
    date: "2020–2024",
    title: "Built & proven in market",
    stats: [
      { prefix: "$", value: 687, suffix: "K", label: "revenue" },
      { value: 62, label: "advisors" },
      { value: 52, suffix: "%", label: "close rate" },
    ],
    body: "Generated over $687K in revenue, onboarded 62 advisors, and validated a 52% close rate.",
  },
  {
    date: "Trailing 12 Mo.",
    title: "Revenue inflection",
    stats: [
      { prefix: "$", value: 1.2, decimals: 1, suffix: "M", label: "trailing-365 cash" },
      { prefix: "+", value: 234, suffix: "%", label: "YoY" },
    ],
    body: "Trailing-365-day cash realization reached $1.2M — up 234% year over year.",
  },
  {
    date: "2025",
    title: "Scaling efficiently",
    stats: [{ prefix: "$", value: 505, suffix: "K", label: "gross revenue booked" }],
    body: "$505K gross revenue booked as the advisor engine and partnerships compounded.",
  },
  {
    date: "Q3 2026",
    title: "$675K premium written",
    stats: [
      { prefix: "$", value: 14.3, decimals: 1, suffix: "K", label: "commissions earned" },
      { value: 7, label: "cases" },
    ],
    body: "Direct agent commissions began flowing — real production, booked and paid across 7 cases in Q3 2026.",
  },
  {
    date: "Partnerships",
    title: "National channel",
    stats: [{ value: 7, label: "strategic partners" }],
    body: "Strategic partners — click a name to see what it brings.",
    partners: [
      { name: "Thurston Capital", desc: "Strategic partner — details available on request.", url: "https://peakbrokerageservices.com/" },
      { name: "Peak", desc: "Strategic partner — details available on request.", url: "https://peakbrokerageservices.com/" },
      { name: "Blackridge", desc: "Strategic partner — details available on request." },
      { name: "Mutual of Omaha Mortgage", desc: "Residential mortgage lending channel.", url: "https://www.mutualofomaha.com/about/newsroom/article/mutual-of-omaha-mortgage-announces-new-strategic-relationship-with-estate-planning-company-to-grow-reverse-mortgage-business" },
      { name: "Simplicity", desc: "Insurance and annuity distribution network.", url: "https://www.simplicitygroup.com/" },
      { name: "Crawford Trust Company", desc: "Trust and fiduciary services.", url: "https://www.crawfordtrust.com/services/successor-trustee/" },
      { name: "Annexus", desc: "Annuity and retirement product design.", url: "https://www.annexus.com/" },
    ],
  },
  {
    date: "Now",
    title: "$2.5M convertible note or direct equity",
    statText: "Open to accredited investors",
    body: "Raising to scale distribution, platform, and recurring subscription revenue.",
  },
];

// Competitor raises + category activity, from user-supplied graphics (publicly reported figures / LinkedIn competitor analytics).
export const COMPARABLES = {
  combined: 274,
  raises: [
    { name: "Wealth.com", amount: "About $111M" },
    { name: "Vanilla", amount: "Over $80M" },
    { name: "Trust & Will", amount: "$83M" },
  ],
  activity: [
    { rank: 1, name: "Wealth.com", followers: "13,006", posts: 207, delta: -18.8 },
    { rank: 2, name: "Vanilla", followers: "22,490", posts: 200, delta: -3.4 },
    { rank: 3, name: "Trust & Will", followers: "18,372", posts: 147, delta: -32.6 },
    { rank: 4, name: "ePIC Services Co.", followers: "417", posts: 37, delta: 146.7, highlight: true },
  ],
};

// Press — Mutual of Omaha Mortgage newsroom announcement (Sept 2023).
export const PRESS = {
  source: "Mutual of Omaha Mortgage Newsroom",
  date: "September 2023",
  quote: "Our new relationship with ePIC Services Company reinforces our commitment to serving the evolving needs of the baby boomer generation.",
  url: "https://www.mutualofomaha.com/about/newsroom/article/mutual-of-omaha-mortgage-announces-new-strategic-relationship-with-estate-planning-company-to-grow-reverse-mortgage-business",
};

// Raise progress — set `committed` (USD) to activate the live progress bar; null shows the "allocations open" state.
export const RAISE_PROGRESS = { committed: 1060000 };

// Investor FAQ — answers use only facts stated elsewhere on this page or in the offering documents.
export const FAQ_ITEMS = [
  {
    q: "What am I investing in?",
    a: "One of two paths. A $2.5M unsecured convertible promissory note paying 10% annual interest in cash, quarterly, with conversion at a fixed $4.50 per share and up to 40% bonus shares — or direct equity at $3.15 per share.",
  },
  {
    q: "What is the minimum investment?",
    a: "$50,000 for the convertible note. $10,000 for direct equity.",
  },
  {
    q: "How and when is interest paid?",
    a: "The note pays 10% annual interest in cash, distributed quarterly over its 5-year term — roughly $50,000 in total interest on a $100,000 note held for the full term.",
  },
  {
    q: "What are my conversion options?",
    a: "Multiple options, including quarterly partial conversions, all at a fixed $4.50 per share. A full-conversion election carries up to 40% bonus shares. The company may also prepay the note at any time without penalty.",
  },
  {
    q: "What happens in a default?",
    a: "The note converts automatically into equity upon default, under the terms of the note agreement.",
  },
  {
    q: "Who can invest?",
    a: "This offering is open to accredited investors only. The entry gate on this site requires confirmation of accredited status before the overview is shown.",
  },
  {
    q: "What are the risks?",
    a: "All investments involve risk, including the possible loss of principal. Review the Forward-Looking Statements & Risk Factors document in full before investing.",
    linkText: "Read the Risk Factors (PDF)",
    linkHref: "/docs/risk-factors.pdf",
  },
  {
    q: "How do I invest?",
    a: "Sign online via Adobe Acrobat Sign — the links are in The Raise and Direct Equity sections — or book a meeting and the team will walk you through it.",
  },
];

// What happens after you invest
export const AFTER_INVEST = [
  { n: "01", title: "Sign online", body: "Complete and e-sign your documents securely via Adobe Acrobat Sign — no account or printing required." },
  { n: "02", title: "Fund your allocation", body: "Funding instructions are delivered with your countersigned documents." },
  { n: "03", title: "Earn along the way", body: "Note holders receive 10% annual interest in cash, paid quarterly. Equity holders hold shares priced at $3.15." },
  { n: "04", title: "The 5-year horizon", body: "The model targets a 5-year window — note repayment or conversion at $4.50 per share with up to 40% bonus shares, and equity participation at the modeled exit values." },
];

// Advisor results spotlight — Jim Crump's first seminar week, message shared verbatim by the owner.
export const ADVISOR_RESULT = {
  quote: "I am blown away… Never seen this kind of interest. The thirst for knowledge on this subject is overwhelming!",
  name: "Jim Crump",
  role: "ePIC Advisor Partner",
  stats: [
    { value: 261, label: "registered" },
    { value: 201, label: "showed up", note: "77% show rate" },
    { value: 21, label: "appointments booked" },
    { value: 40, label: "asked for a call to schedule" },
  ],
};

// Advisor testimonials — excerpts from the owner-supplied review screenshot; do not extend past the source text.
export const TESTIMONIALS = [
  {
    quote: "Partnering with ePIC Services Company was a game-changer. Their Advisor Partner Client Acquisition Kit gave me…",
    name: "Jim W.",
    role: "Independent Insurance Agent",
  },
  {
    quote: "ePIC helped me turn conversations into cases. The combination of Bridge®, BLP®, and the acquisition kit elevated…",
    name: "Heather H.",
    role: "Independent Insurance Agent",
  },
  {
    quote: "Within 30 days of implementing ePIC's Advisor Partner Client Acquisition Kit, I had more qualified appointments than I'd…",
    name: "Denny A.",
    role: "Financial Retirement Planner",
  },
  {
    quote: "I was impressed with how well thought out the process is with Epic, and the team there was professional and diligent.",
    name: "Patrick G. CF2®",
    role: "Certified Financial Fiduciary | My Life Broker",
  },
];

// Leadership cards render in ascending `order`. Change a member's `order` number to reorder the grid.
export const TEAM = [
  {
    name: "Carter Wilcoxson",
    role: "Founder & CEO",
    bio: "For nearly two decades, Carter has helped top advisors deliver safe retirement solutions and scale their practices. He created the eStatePlan™ Portfolio to connect families with the education and white-glove support they need to protect their wealth and legacy.",
    img: "/team/carter.png",
    order: 1,
  },
  {
    name: "Dr. Matthew Eby",
    role: "Executive Chairman",
    bio: "Founder of Nth Degree Financial. 35 years in the estate and trust industry. Co-author of 'The Invisible Threat.'",
    img: "/team/matthew.png",
    order: 2,
  },
  {
    name: "Mariah Post",
    role: "Chief Operating Officer",
    bio: "Leads company operations, implementation, and the specialist support engine behind ePIC's turnkey delivery.",
    img: "/team/mariah.png",
    order: 4,
  },
  {
    name: "Rosalie Perkins",
    role: "Chief Marketing Officer",
    bio: "As CMO, Rosalie shapes the ePIC story and builds the marketing engine that turns awareness into advisor partnerships, giving families a clear path to protecting their legacy.",
    img: "/team/rosalie.png",
    order: 7,
  },
  {
    name: "Ciara Lister, Esq.",
    role: "Chief Counsel",
    bio: "Leads legal, estate-planning compliance, and the attorney-backed framework at the core of ePIC's platform.",
    img: "/team/ciara.png",
    order: 5,
  },
  {
    name: "Earl Hopper",
    role: "Chief Financial Officer",
    bio: "Oversees ePIC's finance, capital strategy, and the financial model underpinning the company's growth.",
    img: "/team/earl.png",
    order: 3,
  },
  {
    name: "Greg Agnos",
    role: "Chief Development Officer",
    bio: "Prepares all ePIC Advisors to deliver a successful estate and legacy planning educational event and client intake interview.",
    img: "/team/greg.png",
    order: 8,
  },
  {
    name: "Holly Halweg",
    role: "Onboarding Team Leader",
    bio: "Guides every new advisor and their clients through the ePIC platform to ensure completion of the eStatePlan™ Portfolio.",
    img: "/team/holly.png",
    order: 11,
  },
  {
    name: "Lisa Camacho",
    role: "Lead Educator",
    bio: "Heads EPIC University, equipping advisors with the training and workshops that drive activation and results.",
    img: "/team/lisa.png",
    order: 12,
  },
  {
    name: "Joanne Eby",
    role: "Chief Compliance Officer",
    bio: "Ensures ePIC operates to the highest regulatory and compliance standards across its estate-planning ecosystem.",
    img: "/team/joanne.png",
    order: 6,
  },
  {
    name: "Christina Wilcoxson",
    role: "VP of Strategic Partnerships",
    bio: "Builds and scales ePIC's strategic partnerships, expanding the advisor and distribution network nationwide.",
    img: "/team/christina.png",
    order: 9,
  },
  {
    name: "Jerry Yen",
    role: "Strategic Advisor",
    bio: "Provides strategic guidance across growth, partnerships, and capital strategy as ePIC scales nationally.",
    img: "/team/jerry.png",
    order: 10,
  },
  {
    name: "Trisha Lagnas",
    role: "Website Development Team Leader",
    bio: "Leads website development, delivering the digital experience that powers ePIC's platform and advisor tools.",
    img: "/team/trisha.png",
    order: 13,
  },
  {
    name: "Bridgette Cooper",
    role: "Advisor Partner Concierge",
    bio: "Serves as the dedicated point of contact for advisor partners, ensuring a white-glove experience at every step.",
    img: "/team/bridgette.png",
    order: 14,
  },
  {
    name: "Randy Cates",
    role: "Video Production Manager",
    bio: "Leads video production, crafting the visual storytelling that powers ePIC's brand and advisor education.",
    img: "/team/randy.png",
    order: 15,
  },
  {
    name: "Caitlyn Wilcoxson",
    role: "Deed Retitlement Team Leader",
    bio: "Leads the deed retitlement team, ensuring client property is accurately retitled into their trusts to complete the estate plan.",
    img: "/team/caitlyn.png",
    order: 16,
  },
];

export const TEAM_ROSTER = [];

export const IMAGES = {
  heroTexture:
    "https://images.unsplash.com/photo-1486551937199-baf066858de7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGRhdGF8ZW58MHx8fHwxNzg3MjI3NTQ2fDA&ixlib=rb-4.1.0&q=85",
  building:
    "https://images.pexels.com/photos/14528986/pexels-photo-14528986.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

// Full Forward-Looking Statements & Risk Factors (verbatim source, condensed per factor).
export const DISCLAIMER_INTRO =
  "These investor materials contain forward-looking statements, projections, estimates, and forecasts regarding the anticipated future performance of ePIC Services Company, Inc. Forward-looking statements are not guarantees of future performance and involve significant risks and uncertainties. Prospective investors should carefully consider the following factors before making an investment decision.";

export const DISCLAIMERS = [
  "Revenue & Growth Risk — Projected revenues and growth rates rely on assumptions about customer acquisition, retention, demand, pricing, and execution. Actual revenues may be materially lower than projected.",
  "Market & Economic Conditions — Changes in economic conditions, interest rates, inflation, spending, credit availability, or other macroeconomic factors may adversely affect operations and financial performance.",
  "Competition — The Company may face competitors with greater financial resources, established relationships, technology, or market presence, which could reduce revenues, margins, or market share.",
  "Operating & Execution Risk — Projections depend on management's ability to execute the business plan, recruit and retain personnel, manage growth, and control costs. Failure to execute could materially affect results.",
  "Key Personnel — Success may depend on continued services of key management and employees. Loss of key personnel could negatively affect the Company.",
  "Capital Requirements & Financing Risk — The Company may require additional capital, which may not be available on acceptable terms. Additional financing may dilute existing investors.",
  "Cost & Margin Risk — Actual labor, technology, insurance, marketing, regulatory, and other costs may exceed assumptions, reducing gross margins, profitability, and cash flow.",
  "Regulatory & Legal Risk — Changes in laws, regulations, licensing, taxes, or industry standards could increase operating costs or otherwise adversely affect the Company.",
  "Technology & Cybersecurity Risk — System failures, cybersecurity incidents, data breaches, or interruptions in critical third-party services could adversely affect operations.",
  "Customer & Concentration Risk — Dependence on a limited number of customers, contracts, suppliers, or strategic relationships means loss of a significant relationship could materially affect revenues.",
  "Projection & Assumption Risk — Financial projections are inherently uncertain. Assumptions may not prove accurate, and actual results may differ materially from projected results.",
  "Investment Risk — An investment in the Company is speculative and involves a substantial risk of loss, including the possible loss of some or all of an investor's investment. No return is assured.",
  "No assurance is given that any projection or forward-looking statement will be achieved. Prospective investors should conduct their own independent due diligence and consult their legal, tax, accounting, and financial advisors before investing.",
];
